import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import type { AccountRole, MeResponse, MembershipDto } from "@cwm/shared";

import {
  apiFetch,
  ApiError,
  setAccessToken,
  sharedRefresh,
} from "./api";

/**
 * React auth context. Holds the resolved `MeResponse` (null if anonymous) plus
 * `loading` while the initial `/auth/me` probe is in flight. The actual
 * access-token value lives in the `api` module so every request picks it up
 * automatically — the context only exposes user-shaped state.
 *
 * Multi-tenant accounts (plan §6): the Me shape carries `memberships`,
 * `activeAccountId`, `isSuperAdmin`, and `mustChangePassword`. We expose
 * these as first-class fields on the context plus two helpers:
 *   - `roleInActiveAccount()` — resolves the caller's role in the account
 *      currently in scope (null when no active account, e.g. super admin
 *      without a membership).
 *   - `hasAccountRole(role)` — true when the active role matches OR the
 *      caller is a super admin (platform-level override).
 */

export interface AuthContextValue {
  user: MeResponse | null;
  loading: boolean;
  memberships: MembershipDto[];
  activeAccountId: string | null;
  isSuperAdmin: boolean;
  mustChangePassword: boolean;
  /**
   * Super-admin "view as account" — non-null when the current super-admin
   * session has picked a tenant account to impersonate. Drives the amber
   * ViewAsBanner and gates the "Exit" affordance.
   */
  viewAsAccountId: string | null;
  viewAsAccountName: string | null;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  /**
   * Trigger a password-reset email. Resolves with no value regardless of
   * whether the email matches an account — the server is anti-enumeration
   * by design. Rejects only on network failure or 429 (rate limit).
   */
  requestPasswordReset(email: string): Promise<void>;
  /**
   * Consume a reset-link token and set a new password. Rejects with the
   * usual {@link ApiError} on 400 (invalid token or weak password).
   * Side effect: the server revokes every other session for the user;
   * the local context is NOT touched (the caller is on the login flow,
   * not signed in yet).
   */
  resetPassword(token: string, newPassword: string): Promise<void>;
  /**
   * Force-refresh `user` from `/auth/me`. Callers (e.g. Settings page) use
   * this after a PATCH to /users/me so the cached context picks up the new
   * preferred currency without a full page reload.
   */
  refreshUser(): Promise<void>;
  /**
   * Caller's role in the currently active account. `null` when there is no
   * active account or the caller has no membership there (common for super
   * admins browsing a tenant they don't belong to).
   */
  roleInActiveAccount(): AccountRole | null;
  /**
   * True when the active role matches `role` or the caller is a super admin.
   * Super admin ALWAYS passes — it's the platform-level role-override rule
   * documented in plan §6.
   */
  hasAccountRole(role: AccountRole): boolean;
  /**
   * Exit super-admin view-as mode: POSTs `/auth/switch-account` with
   * `mode: "global"`, swaps the freshly-minted token, then hard-reloads so
   * every in-flight query is re-fetched under cross-tenant scope. The same
   * URL is preserved so the super admin lands back on the page they were
   * viewing as the tenant. No-op (silently) when not in view-as mode.
   */
  exitViewAs(): Promise<void>;
}

// Exported so out-of-tree consumers (e.g. WalletDirectoryProvider in tests
// that don't mount AuthProvider) can read the context tolerantly via
// `useContext(AuthContext)` and fall back when null.
export const AuthContext = createContext<AuthContextValue | null>(null);

interface LoginResponse {
  accessToken: string;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const login = useCallback(async (email: string, password: string) => {
    const { accessToken } = await apiFetch<LoginResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
      skipRefresh: true,
    });
    setAccessToken(accessToken);
    const me = await apiFetch<MeResponse>("/auth/me");
    setUser(me);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch<void>("/auth/logout", { method: "POST" });
    } catch {
      // Logout should still clear local state even if the network call fails.
    }
    setAccessToken(null);
    setUser(null);
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    // Anti-enumeration on the wire (200 on every match outcome) is the
    // server's job — we just forward the call and let the caller branch
    // on rare 429 / network errors via ApiError.
    await apiFetch<void>("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
      skipRefresh: true,
    });
  }, []);

  const resetPassword = useCallback(
    async (token: string, newPassword: string) => {
      await apiFetch<void>("/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token, newPassword }),
        skipRefresh: true,
      });
    },
    [],
  );

  const refreshUser = useCallback(async () => {
    try {
      const me = await apiFetch<MeResponse>("/auth/me");
      setUser(me);
    } catch {
      // Leave the current user in place on failure — callers decide how to
      // surface the error (usually via a toast from the PATCH itself).
    }
  }, []);

  useEffect(() => {
    // Boot sequence — kill the "logout flicker" on hard reload.
    //
    // The in-memory access token is gone after a refresh, so probing
    // `/auth/me` directly would 401 with `UNAUTHENTICATED` (not
    // `TOKEN_EXPIRED`), and the api.ts 401-interceptor only auto-refreshes
    // on `TOKEN_EXPIRED`. So we explicitly run a silent refresh first via
    // the cookie. If it succeeds, the access token is in place and the
    // `/auth/me` probe will carry the bearer header. If it fails, the user
    // is anonymous; `<ProtectedRoute>` will redirect to /login.
    //
    // Only `setUser` is called once with the final outcome — no
    // intermediate setUser(null) before the refresh resolves, so
    // `<ProtectedRoute>` (which gates on `loading`) stays on the spinner
    // and never paints a logged-out frame.
    let cancelled = false;
    (async () => {
      const refreshed = await sharedRefresh();
      if (cancelled) return;
      if (!refreshed) {
        setUser(null);
        setLoading(false);
        return;
      }
      try {
        const me = await apiFetch<MeResponse>("/auth/me");
        if (!cancelled) setUser(me);
      } catch {
        // Refresh succeeded but /me failed (e.g. user deactivated between
        // refresh and probe, or transient network error). Treat as
        // anonymous — same outcome the user would see on next nav.
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // `memberships` has to be referentially stable across renders: downstream
  // consumers (AccountSwitcher, AccountMembers) put it in hook dep arrays,
  // and a fresh `[]` on every render would invalidate their memoisation.
  const memberships = useMemo<MembershipDto[]>(
    () => user?.memberships ?? [],
    [user?.memberships],
  );
  const activeAccountId = user?.activeAccountId ?? null;
  const isSuperAdmin = user?.isSuperAdmin ?? false;
  const mustChangePassword = user?.mustChangePassword ?? false;
  const viewAsAccountId = user?.viewAsAccountId ?? null;
  const viewAsAccountName = user?.viewAsAccountName ?? null;

  const exitViewAs = useCallback(async () => {
    // No-op when nothing to exit (defensive — callers shouldn't reach this
    // when not in view-as mode, but a stale hover/click race could).
    if (!viewAsAccountId) return;
    // The /auth/switch-account endpoint requires an accountId. In "global"
    // mode the value is only used to update `lastAccountId` (server-side
    // hint for next login). Re-using the impersonated id is fine: tenant
    // membership isn't checked for super admins, and the server validates
    // that the account exists & isn't soft-deleted.
    const { accessToken } = await apiFetch<{ accessToken: string }>(
      "/auth/switch-account",
      {
        method: "POST",
        body: JSON.stringify({ accountId: viewAsAccountId, mode: "global" }),
      },
    );
    setAccessToken(accessToken);
    // Hard reload so every page re-fetches under cross-tenant scope. Same
    // URL on purpose: the super admin lands back where they left off but
    // now sees the all-tenants aggregate view.
    window.location.reload();
  }, [viewAsAccountId]);

  const roleInActiveAccount = useCallback((): AccountRole | null => {
    if (!activeAccountId) return null;
    const match = memberships.find((m) => m.accountId === activeAccountId);
    return match ? match.role : null;
  }, [memberships, activeAccountId]);

  const hasAccountRole = useCallback(
    (role: AccountRole): boolean => {
      if (isSuperAdmin) return true;
      return roleInActiveAccount() === role;
    },
    [isSuperAdmin, roleInActiveAccount],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      memberships,
      activeAccountId,
      isSuperAdmin,
      mustChangePassword,
      viewAsAccountId,
      viewAsAccountName,
      login,
      logout,
      requestPasswordReset,
      resetPassword,
      refreshUser,
      roleInActiveAccount,
      hasAccountRole,
      exitViewAs,
    }),
    [
      user,
      loading,
      memberships,
      activeAccountId,
      isSuperAdmin,
      mustChangePassword,
      viewAsAccountId,
      viewAsAccountName,
      login,
      logout,
      requestPasswordReset,
      resetPassword,
      refreshUser,
      roleInActiveAccount,
      hasAccountRole,
      exitViewAs,
    ],
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}

// Re-export ApiError so callers can switch on `error.code` without importing
// the api module separately.
export { ApiError };
